/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from "react";
import { requestSMSSending } from "@/api/sms";
import { RequestValue, useOptions } from "@/api/options";
import { useFormStore } from "@/store/form";
import { useProductStore } from "@/store/product";
// Core
import { autoDebtDataSchema, invoiceSchema } from "@/components/AutoDebt";
import { creditCardDataSchema } from "@/components/CreditCard";

import api from "@/api";
import { SHORT_MESSAGE } from "@/api/shortMessages";
import { markIntentionalFlowPause } from "@/utils/sentry/intentional";
import { downloadPdfFile } from "@/utils/blob";
import {
  removeAccentAndSpace,
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { requestCreateSale, SaleRawData } from "@/api/sale";
import { requestScheduleActivation } from "@/api/sale/scheduleActivation";
import {
  requestEligibility,
  requestEligibilityExpress,
} from "@/api/eligibility/eligibility";
import { sendUserJourney } from "@/api/userJourney";
import { findCorrespondentByDDD, getOnlyNumbers } from "@/utils";
import { ProductsFetch } from "@/api/products";
import { requestTokenizeCard } from "@/api/m4u/tokenizeCard";
import { requestSaveTokenizedCard } from "@/api/sale/saveTokenizedCard";
import { requestUpdatePaymentData } from "@/api/sale/updatePaymentData";
import { requestActivateSale } from "@/api/sale/activateSale";
import { requestInitM4UAcquisition } from "@/api/sale/initM4UAcquisition";
import { usePaymentMethodChangedStore } from "@/store/paymentMethodChanged";
import usePageSubmit from "@/hooks/usePageSubmit";
import { requestAnalysis } from "@/api/eligibility/analysis";
import { useModalStore } from "@/store/modal";
import useVerifySMSConfirmationIsOn from "@/hooks/useVerifySMSConfimationIsOn";
import { formatPrice } from "@/utils/currency";
import { GenericObject } from "@/utils/types";
import { useConfigStore } from "@/store/config";

export interface Payment {
  value: string;
  label: string;
}

export interface Payments {
  debitoautomatico: Payment;
  fatura: Payment;
  cartaodecredito: Payment;
  [key: string]: Payment;
}

export const payments: Payments = {
  debitoautomatico: {
    value: "Débito Automatico",
    label: "Débito Automático",
  },
  fatura: {
    value: "Fatura",
    label: "Conta Digital",
  },
  cartaodecredito: {
    value: "Cartão de Crédito",
    label: "Cartão de Crédito",
  },
};

type PageConfig = {
  route: string;
  prevRoute: string;
  nextRoute: string;
};

// Page Config
export const pageConfigDefault = {
  route: "/payment-data",
  prevRoute: "/address-info",
  nextRoute: "/payment-data/sms",
};

export const DEBITO = `Débito Automatico`;
export const CREDITO = `Cartão de Crédito`;
export const FATURA = `Fatura`;
export const SANITIZED_CREDITO = "cartaodecredito";
export const SANITIZED_DEBITO = "debitoautomatico";
export const SANITIZED_FATURA = "fatura";

// Bindings locais para SHORT_MESSAGE — fonte unica no modulo shortMessages.
const CORRESPONDENT_AVAILABLE_ERROR_MESSAGE = SHORT_MESSAGE.CORRESPONDENT_AVAILABLE;
const UNAUTHORIZED_TRANSACTION = SHORT_MESSAGE.UNAUTHORIZED_TRANSACTION;

export const barCode = (
  <svg
    width="25"
    height="24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#currentColor"
      d="M1 3h3v15H1V3zm4 0h2v15H5V3zm3 0h2v15H8V3zm5 0h1v15h-1V3zm6 0h1v15h-1V3zm4 0h2v15h-2V3zm-7 0h1v15h-1V3zm-5 0h1v15h-1V3zm10 0h1v15h-1V3zM1 20h1v1H1v-1zm4 0h2v1H5v-1zm3 0h2v1H8v-1zm8 0h1v1h-1v-1zm7 0h2v1h-2v-1zm-4 0h3v1h-3v-1zm-8 0h3v1h-3v-1z"
    />
  </svg>
);

export const creditCard = (
  <svg
    width="25"
    height="24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g>
      <path
        d="M22 3H2.5A2.257 2.257 0 0 0 .25 5.25v13.5A2.257 2.257 0 0 0 2.5 21H22a2.257 2.257 0 0 0 2.25-2.25V5.25A2.257 2.257 0 0 0 22 3zM2.5 4.5H22a.76.76 0 0 1 .75.75V7.5h-21V5.25a.76.76 0 0 1 .75-.75zm19.5 15H2.5a.76.76 0 0 1-.75-.75V11h21v7.75a.76.76 0 0 1-.75.75zM3.25 15h1.5v3h-1.5v-3zm3 0h1.5v3h-1.5v-3zm3 0h1.5v3h-1.5v-3z"
        fill="#currentColor"
      />
    </g>
  </svg>
);

// Payment Info Data
export const usePaymentInfoData = () => {
  const addBonusPortability = useProductStore(
    (store) => store.addBonusPortability
  );
  const removeBonusPortability = useProductStore(
    (store) => store.removeBonusPortability
  );
  const { data: products } = ProductsFetch();
  const shouldAddSpecialBonus = useProductStore(
    (store) => store.shouldAddSpecialBonus
  );
  const setProduct = useProductStore((store) => store.setProduct);
  const isPortability = useFormStore((store) => store.isPortability);
  const isMigration = useFormStore((store) => store.isMigration);
  const phoneNumber = useFormStore((store) => store.value.tel);
  const ddd = phoneNumber.slice(0, 2);
  const smsFromPortabilityIsOn = useVerifySMSConfirmationIsOn(
    ddd,
    isPortability()
  );
  const validationStepIsOn = isMigration() ? true : smsFromPortabilityIsOn;
  const { result: data } = useOptions();
  const paymentFormStore = useFormStore((store) => store.value.payment);
  const [paymentTypes, setPaymentTypes] = useState<Payment[]>([]);
  const paymentMethods = useMemo(
    () => paymentTypes.filter((paymentType) => paymentType.value !== DEBITO),
    [paymentTypes]
  );
  const [knownTerms, setKnownTerms] = useState(paymentFormStore?.knownTerms ?? true);
  const [knownClausules, setKnownClausules] = useState(
    paymentFormStore?.knownClausules ?? true
  );
  const [formIsValid, setFormIsValid] = useState(false);
  const [formPaymentIsValid, setFormPaymentIsValid] = useState(false);
  const form = useFormStore((store) => store.value);
  const product = useProductStore((store) => store.item);
  const payment = useProductStore((store) => store.payment);
  const setPayment = useProductStore((store) => store.setPayment);
  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());
  const addData = useFormStore((store) => store.addData);
  const isExpress = useProductStore((store) => store.isExpress);
  const paymentMethodHasAlreadyBeChanged = usePaymentMethodChangedStore(
    (store) => store.paymentMethodHasAlreadyBeChanged
  );
  const shouldUpdateSale = usePaymentMethodChangedStore(
    (store) => store.shouldUpdateSale
  );
  const setShouldUpdateSale = usePaymentMethodChangedStore(
    (store) => store.setShouldUpdateSale
  );
  const setShouldHideCreditCard = usePaymentMethodChangedStore(
    (store) => store.setShouldHideCreditCard
  );
  const setShouldHideAutomaticDebitAndBilling = usePaymentMethodChangedStore(
    (store) => store.setShouldHideAutomaticDebitAndBilling
  );
  const setShowAutoDebtPayment = usePaymentMethodChangedStore(
    (store) => store.setShowAutoDebtPayment
  );
  const setShowModalCustumerValidation = useModalStore(
    (store) => store.setShowModalCustomerValidation
  );
  const setUrlValidation = useModalStore((store) => store.setUrlValidation);

  const [showForceChangePaymentMethod, setShowForceChangePaymentMethod] =
    useState(false);
  const [userSaidThatDontWantToChange, setUserSaidThatDontWantToChange] =
    useState(false);
  const [showUnauthorizedAlert, setShowUnauthorizedAlert] = useState(false);
  const [method, setMethod] = useState("");

  const paymentDataSchema = useMemo(() => {
    switch (payment?.value) {
      case DEBITO:
        return autoDebtDataSchema;
      case CREDITO:
        return creditCardDataSchema;
      case FATURA:
        return invoiceSchema;
      default:
        return invoiceSchema;
    }
  }, [payment?.value]);

  const [sendSMSIsLoading, setSendSMSIsLoading] = useState(false);
  const [sendSMSerror, setSendSMSerror] = useState();
  const [saleError, setError] = useState(false);
  const saleId = useFormStore((store) => store.saleId);
  const setSaleId = useFormStore((store) => store.setSaleId);
  const loyalty = useProductStore((store) => store.loyalty);
  const setLoyalty = useProductStore((store) => store.setLoyalty);
  const [sendSaleLoading, setSaleLoading] = useState(false);
  const [scheduleActivationLoading, setScheduleActivationLoading] =
    useState(false);
  const [activateExpressLoading, setActivateExpressLoading] = useState(false);
  const [initM4UAcquisitionLoading, setInitM4UAcquisitionLoading] =
    useState(false);

  const [updatePaymentDataLoading, setUpdatePaymentDataLoading] =
    useState(false);
  const [m4uStatusLoading, setM4uStatusLoading] = useState(false);
  const [eligibilityStatusLoading, setEligibilityStatusLoading] =
    useState(false);
  const [tokenizerCardStatusLoading, setTokenizerCardStatusLoading] =
    useState(false);
  const [analysisStatusLoading, setAnalysisStatusLoading] = useState(false);

  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(form.tel).slice(0, 2))
  );

  const shortFlowOn = useConfigStore((store) => store.shortFlowOn);

  const [pageConfig, setPageConfig] = useState<PageConfig>(pageConfigDefault);

  const [nextRoute, setNextRoute] = useState(pageConfig.nextRoute);

  const downloadRegulationPlan = () => {
    const params = {
      tim_id: product?.tim_id,
      path_regulamento: "regulamento",
      time: new Date().getTime(),
    };
    return api
      .get(`/core/pdf/download-regulamento`, {
        responseType: "blob",
        params,
      })
      .then((response) => {
        const file = response.data;
        const filename = `regulamento-${product?.tim_id}`;
        downloadPdfFile({ filename, file });
      });
  };

  const downloadRegulation = (path: string, slug: string) => {
    const idConfigPackage = product?.pacote_configuracoes_id_substituto
      ? product?.pacote_configuracoes_id_substituto
      : product?.pacote_configuracoes.id;

    const params = {
      id: idConfigPackage,
      path_regulamento: path,
      tim_id: !idConfigPackage && product?.tim_id,
      time: new Date().getTime(),
    };
    return api
      .get(`/core/pdf/download-regulamento`, {
        responseType: "blob",
        params,
      })
      .then((response) => {
        const file = response.data;
        const filename = `regulamento-${slug}`;
        downloadPdfFile({ filename, file });
      });
  };

  const continueButtonText = () => {
    if (userSaidThatDontWantToChange) {
      return "Finalizar Pedido";
    }
    return validationStepIsOn ? "Continuar" : "Finalizar Pedido";
  };

  const isLoading =
    sendSMSIsLoading ||
    sendSaleLoading ||
    m4uStatusLoading ||
    tokenizerCardStatusLoading ||
    eligibilityStatusLoading ||
    analysisStatusLoading ||
    activateExpressLoading ||
    initM4UAcquisitionLoading ||
    scheduleActivationLoading ||
    updatePaymentDataLoading;

  // Plano correspondente (CONTROLE <-> EXPRESS) vendavel para o DDD do
  // cliente. undefined quando o correspondente esta inativo ou sem preco
  // para o DDD — nesse caso a troca de forma de pagamento nao e oferecida.
  const correspondentProduct = useMemo(
    () =>
      findCorrespondentByDDD(
        products,
        product?.correspondente,
        getOnlyNumbers(form.tel).slice(0, 2)
      ),
    [products, product?.correspondente, form.tel]
  );
  const hasAvailableCorrespondent = correspondentProduct !== undefined;

  // Retorna false quando a troca exige o correspondente e ele nao esta
  // disponivel — o chamador nao deve mudar a forma de pagamento.
  const loadNewProductIfNecessary = useCallback(
    (value: Payment): boolean => {
      const key = removeAccentAndSpace(value.value);

      const correspondent = product?.correspondente;

      const needToBeChanged =
        correspondent !== null &&
        ((isExpress() && key !== SANITIZED_CREDITO) ||
          (!isExpress() && key === SANITIZED_CREDITO));

      if (!needToBeChanged) {
        return true;
      }

      if (!correspondentProduct) {
        return false;
      }

      setProduct(correspondentProduct);
      setLoyalty(true);

      if (form.tipo === "PORTABILIDADE" || shouldAddSpecialBonus()) {
        addBonusPortability();
      } else {
        removeBonusPortability();
      }

      return true;
    },
    [
      addBonusPortability,
      correspondentProduct,
      form.tipo,
      isExpress,
      product?.correspondente,
      removeBonusPortability,
      setLoyalty,
      setProduct,
      shouldAddSpecialBonus,
    ]
  );

  const handleOnChangePaymentTypeUserJourney = useCallback(
    (value: any) => {
      addData({
        paymentType: value,
      });
      sendUserJourney({
        ...form,
        product: product!,
        loyalty,
        paymentType: value,
        passo: "PAGAMENTO",
      });
    },
    [addData, form, product, loyalty]
  );

  const changePaymentMethod = useCallback(
    (value: Payment) => {
      // Primeiro garante o plano compativel; so entao muda a forma de
      // pagamento. Evita ficar com "Cartao" selecionado num plano CONTROLE.
      if (!loadNewProductIfNecessary(value)) {
        return;
      }
      setPayment(value as RequestValue);
      handleOnChangePaymentTypeUserJourney(value);
    },
    [
      handleOnChangePaymentTypeUserJourney,
      loadNewProductIfNecessary,
      setPayment,
    ]
  );

  const handleChangePaymentMethod = () => {
    if (isExpress()) {
      changePaymentMethod(payments.fatura);
      setShouldHideCreditCard(true);
    } else {
      changePaymentMethod(payments.cartaodecredito);
      setShouldHideAutomaticDebitAndBilling(true);
    }
  };

  const declareThatIsNeedToChangePaymentMethodIfPossible = (err: any) => {
    if (paymentMethodHasAlreadyBeChanged) {
      return;
    }

    if (!hasAvailableCorrespondent) {
      return;
    }

    setShowForceChangePaymentMethod(true);

    let methodToChangeString = "Cartão de Crédito";

    if (isExpress()) {
      methodToChangeString = "Débito Automático ou Conta Digital";
    }

    setMethod(methodToChangeString);

    // Throw apenas para "pausar" o fluxo enquanto o modal eh exibido — o
    // usePageSubmit usa rejeicao da promise como sinal de "nao avancar".
    // Marcado como intencional para nao poluir o Sentry.
    throw markIntentionalFlowPause(err);
  };

  const verifyIfIsNotAuthorizedTransaction = (err: any) => {
    const isNotExpress = !(
      product?.segmento === "EXPRESS" || product?.segmento === "POS_EXPRESS"
    );

    if (isNotExpress) {
      return;
    }

    const isNotUnauthorized =
      err?.response?.data?.shortMessage !== UNAUTHORIZED_TRANSACTION;

    if (isNotUnauthorized) {
      return;
    }

    setShowUnauthorizedAlert(true);

    // Throw apenas para "pausar" o fluxo enquanto o alert eh exibido — o
    // usePageSubmit usa rejeicao da promise como sinal de "nao avancar".
    // Marcado como intencional para nao poluir o Sentry.
    throw markIntentionalFlowPause(err);
  };

  const createOrUpdateSale = async (
    saleID: number | null,
    variables: SaleRawData
  ): Promise<number> => {
    if (!saleID) {
      setSaleLoading(true);
      await requestCreateSale(variables)
        .then(async (response: GenericObject) => {
          saleID = response.id;
          setSaleId(response.id);
        })
        .finally(() => {
          setSaleLoading(false);
        });
    }

    if (shouldUpdateSale) {
      setUpdatePaymentDataLoading(true);
      await requestUpdatePaymentData(variables).finally(() =>
        setUpdatePaymentDataLoading(false)
      );
      setShouldUpdateSale(false);
    }

    return saleID!;
  };

  const performEligibilityHandlingWithReturn = async (saleID: number) => {
    setEligibilityStatusLoading(true);

    let errorOnEligibility = false;

    await requestEligibility({
      venda_id: saleID,
    })
      .catch((err) => {
        const shortMessage = err?.response?.data?.shortMessage;

        errorOnEligibility = true;
        if (shortMessage === CORRESPONDENT_AVAILABLE_ERROR_MESSAGE) {
          declareThatIsNeedToChangePaymentMethodIfPossible(err);
        }
      })
      .finally(() => setEligibilityStatusLoading(false));

    if (errorOnEligibility) {
      setEligibilityStatusLoading(true);
      const data = await requestEligibilityExpress({
        venda_id: saleID,
      })
        .catch((err) => {
          // Erro ja foi reportado pelo interceptor do axios. Re-throw para
          // o outer catch do handleSubmit fazer setError(true) — sem isso,
          // o usuario avancaria silenciosamente sem modal nem mensagem.
          throw err;
        })
        .finally(() => setEligibilityStatusLoading(false));

      if (data) {
        declareThatIsNeedToChangePaymentMethodIfPossible({
          response: {
            data: {
              shortMessage: CORRESPONDENT_AVAILABLE_ERROR_MESSAGE,
            },
          },
        });
      }
    }
  };

  const performAnalysisHandlingWithReturn = async (saleID: number) => {
    setAnalysisStatusLoading(true);
    await requestAnalysis({
      venda_id: saleID,
    })
      .then((response) => {
        if (response?.url) {
          setShowModalCustumerValidation(true);
          setUrlValidation(response?.url);
        }
      })
      .finally(() => setAnalysisStatusLoading(false));
  };

  const finalizeOrderAfterEligibility = async (
    saleID: number,
    newData: GenericObject
  ) => {
    if (isExpress()) {
      try {
        await tokenizeM4UCreditCardSavingToken(
          form.cpf,
          saleID!,
          newData.payment
        );

        if (isMigration()) {
          setActivateExpressLoading(true);
          await requestActivateSale({
            venda_id: saleID!,
            cliente_cpf: form.cpf,
          }).finally(() => setActivateExpressLoading(false));
        } else {
          setInitM4UAcquisitionLoading(true);
          await requestInitM4UAcquisition({
            venda_id: saleID!,
            cliente_cpf: form.cpf,
          }).finally(() => setInitM4UAcquisitionLoading(false));
        }
      } catch (err) {
        verifyIfIsNotAuthorizedTransaction(err);
      }
    }

    if (validationStepIsOn) {
      setSendSMSIsLoading(true);
      requestSMSSending({ saleID: saleID!, msisdn: phoneNumber })
        .catch((error) => {
          // Erro ja capturado pelo interceptor global do axios.
          setSendSMSerror(error);
        })
        .finally(() => setSendSMSIsLoading(false));
    } else {
      setScheduleActivationLoading(true);
      requestScheduleActivation(saleID!)
        .catch(() => {
          // Erro ja capturado pelo interceptor do axios. Sem catch a
          // promise vira UnhandledRejection e o cliente avanca sem aviso
          // de que o agendamento falhou.
          setError(true);
        })
        .finally(() => setScheduleActivationLoading(false));
    }

    window?.dataLayer?.push({
      event: "step",
      passo: "PAGAMENTO",
      sku: removeAccentsAndSetUpperCase(product?.tim_id),
      nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
        product?.nome
      )}-${totalGbProduct}`,
      valor: formatPrice(currentPrice),
      status: "Sucesso",
    });
  };

  const tokenizeM4UCreditCardSavingToken = async (
    cpf: string,
    saleID: number,
    paymentData: GenericObject
  ) => {
    let token = "";

    setM4uStatusLoading(true);

    await requestTokenizeCard({
      cartao_numero: paymentData.creditCard,
      cartao_mes: paymentData.dueMonth,
      cartao_ano: paymentData.dueYear,
    })
      .then(async (response) => {
        token = response.token;
      })
      .finally(() => setM4uStatusLoading(false));

    setTokenizerCardStatusLoading(true);
    await requestSaveTokenizedCard({
      venda_id: saleID,
      cliente_cpf: cpf,
      cartao_tokenizado: token,
      cartao_cvv: paymentData.securityCode,
    }).finally(() => setTokenizerCardStatusLoading(false));
  };

  const handleSubmit = async (newData: GenericObject) => {
    if (userSaidThatDontWantToChange && saleId) {
      await finalizeOrderAfterEligibility(saleId, newData);
      return;
    }
    // @ts-expect-error apenas ignora
    nstim.validate("user_prefs");

    // @ts-expect-error apenas ignora
    const user_prefs = document.getElementById("user_prefs")?.value;

    // @ts-expect-error apenas ignora
    const user_prefs2 = document.getElementById("user_prefs2")?.value;

    const userPrefs = {
      user_prefs,
      user_prefs2,
    };

    try {
      const paymentType = {
        value: newData?.paymentType?.value ?? payment?.value,
        label: newData?.paymentType?.label ?? payment?.label,
        type: newData?.paymentType?.type ?? payment?.type,
      };

      let saleID = saleId ?? null;

      saleID = await createOrUpdateSale(saleID, {
        ...form,
        ...newData,
        loyalty,
        paymentType,
        product: product!,
        saleID,
        userPrefs,
      });

      await performEligibilityHandlingWithReturn(saleID);
      if (!validationStepIsOn) {
        await performAnalysisHandlingWithReturn(saleID);
      }

      await finalizeOrderAfterEligibility(saleID, newData);
    } catch (err) {
      setError(true);

      if (
        // @ts-expect-error - TS2339
        err?.response?.data?.shortMessage ===
        CORRESPONDENT_AVAILABLE_ERROR_MESSAGE
      ) {
        setError(false);
      }

      dataLayer.push({
        event: "step",
        passo: "PAGAMENTO",
        sku: removeAccentsAndSetUpperCase(product?.tim_id),
        nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
          product?.nome
        )}-${totalGbProduct}`,
        valor: formatPrice(currentPrice),
        status: "Erro",
      });

      // Throw apenas para "pausar" o fluxo apos exibir erro (modal/alert).
      // O erro real ja foi capturado pelo interceptor do axios — marcado
      // como intencional para nao duplicar no Sentry.
      throw markIntentionalFlowPause(err);
    }
  };

  const handleOnSubmit = usePageSubmit(nextRoute, handleSubmit);

  const handleClickSetPaymentMethod = (value: string) => {
    const valor = {
      type: value,
      value,
      label: value,
    };
    if (value === FATURA) {
      setShowAutoDebtPayment(false);
    }
    changePaymentMethod(valor);
  };

  useEffect(() => {
    if (userSaidThatDontWantToChange && saleId && !shortFlowOn) {
      setPageConfig((prev) => ({
        ...prev,
        nextRoute: "/confirmation/auto-debt",
      }));
    }
  }, [saleId, shortFlowOn, userSaidThatDontWantToChange]);

  return {
    pageConfig,
    setKnownClausules,
    setKnownTerms,
    setFormIsValid,
    knownClausules,
    knownTerms,
    formPaymentIsValid,
    data,
    setPaymentTypes,
    paymentTypes,
    validationStepIsOn,
    setNextRoute,
    showForceChangePaymentMethod,
    setShowForceChangePaymentMethod,
    userSaidThatDontWantToChange,
    setUserSaidThatDontWantToChange,
    handleChangePaymentMethod,
    method,
    showUnauthorizedAlert,
    setShowUnauthorizedAlert,
    handleOnSubmit,
    paymentMethods,
    handleClickSetPaymentMethod,
    hasAvailableCorrespondent,
    setFormPaymentIsValid,
    formIsValid,
    sendSMSerror,
    saleError,
    isLoading,
    continueButtonText,
    downloadRegulationPlan,
    downloadRegulation,
    paymentDataSchema,

    changePaymentMethod,
    performEligibilityHandlingWithReturn,
    performAnalysisHandlingWithReturn,
    isExpress,
    tokenizeM4UCreditCardSavingToken,
    setActivateExpressLoading,
    requestActivateSale,
    requestInitM4UAcquisition,
    setInitM4UAcquisitionLoading,
    verifyIfIsNotAuthorizedTransaction,
    payment,
    payments,
    setShouldHideAutomaticDebitAndBilling,
    setSendSMSIsLoading,
    requestSMSSending,
    setSendSMSerror,
    setScheduleActivationLoading,
    setUpdatePaymentDataLoading,
  };
};
